import { Box, Divider, SwipeableDrawer, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { type FC, useMemo } from "react";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { backendService } from "../../../services/backend.service";
import { INK, NEUTRAL } from "../../../theme/tokens";
import { NotificationType, type ReplaceShiftRequest, RequestStatus, type TransformedShift } from "../../../utils/models";

interface SwapRequestDrawerProps {
	open: boolean;
	onClose: () => void;
	shift: TransformedShift;
}

const SwapRequestDrawer: FC<SwapRequestDrawerProps> = ({ open, onClose, shift }) => {
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { roster, getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const { refreshRequests, staffRequests } = useRequestsContext();
	const { track } = useAnalytics();

	const swapRequests = useMemo(() => {
		return staffRequests.filter(
			(r) => r.request_type === NotificationType.Swap && (r as ReplaceShiftRequest).request_target_shift === shift.id && r.status === RequestStatus.Active,
		) as ReplaceShiftRequest[];
	}, [staffRequests, shift.id]);

	const swapRequestDetails = useMemo(() => {
		return swapRequests.map((request) => {
			const requestingNurse = roster?.find((rosterNurse) => rosterNurse.userId === request.created_by);

			return {
				request,
				requestingNurse,
			};
		});
	}, [swapRequests, roster]);

	const { mutateAsync: acceptSwap, isPending: isAccepting } = useMutation({
		mutationFn: async ({ requestId, shiftId }: { requestId: string; shiftId: string }) => {
			return await backendService.pickOpenShift({
				openShiftId: requestId,
				departmentId: chosenDepartment.department_id,
				targetShiftId: shiftId,
			});
		},
		throwOnError: false,
	});

	const { mutateAsync: declineSwap, isPending: isDeclining } = useMutation({
		mutationFn: async (requestId: string) => {
			return await backendService.declineSwapRequest(requestId, chosenDepartment.department_id);
		},
		throwOnError: false,
	});

	const isPicking = isAccepting || isDeclining;

	const handleApprove = async (requestId: string, shiftId: string): Promise<void> => {
		try {
			track("swap_request_approved", {
				request_id: requestId,
				target_shift_id: shiftId,
				department_id: chosenDepartment.department_id,
			});

			await acceptSwap({
				requestId,
				shiftId,
			});

			showSnackbar("Swap request approved successfully", "success");
			await refreshRequests();
			onClose();
		} catch (error) {
			showErrorSnackbar("Failed to approve swap request", { request_id: requestId }, error);
		}
	};

	const handleDecline = async (requestId: string): Promise<void> => {
		try {
			track("swap_request_declined", {
				request_id: requestId,
				department_id: chosenDepartment.department_id,
			});

			await declineSwap(requestId);

			showSnackbar("Swap request declined successfully", "success");
			await refreshRequests();
			onClose();
		} catch (error) {
			showErrorSnackbar("Failed to decline swap request", { request_id: requestId }, error);
		}
	};

	return (
		<SwipeableDrawer
			data-testid="swap-request-drawer"
			anchor="bottom"
			open={open}
			onOpen={() => {}}
			onClose={onClose}
			slotProps={{
				paper: {
					sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 3, maxHeight: "80vh" },
				},
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<Typography variant="h6" sx={{ fontWeight: 600, color: INK.SECONDARY }}>
					Swap Shift Requests
				</Typography>
				<Divider />

				<Box sx={{ display: "flex", flexDirection: "column", gap: 3, overflowY: "auto" }}>
					{swapRequestDetails.map(({ request, requestingNurse }) => (
						<Box
							key={request.id}
							data-testid={`swap-request-card-${request.id}`}
							sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 2, borderBottom: `1px solid ${NEUTRAL[300]}` }}
						>
							{requestingNurse && (
								<Box>
									<Typography variant="body2" sx={{ color: INK.SECONDARY, fontWeight: 600, mb: 0.5 }}>
										{getNurseFormattedName(requestingNurse.id)} wants to swap
									</Typography>
								</Box>
							)}
							<Box>
								<Typography variant="body2" sx={(theme) => ({ color: theme.palette.primary.main })}>
									{`${request.message.date.toFormat("ccc, MMM d")} • ${request.message.shift_start.toFormat("h:mm a")} - ${request.message.shift_end.toFormat("h:mm a")}`}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", gap: 2, mt: 1 }}>
								<SystemButton
									data-testid={`request-reject-${request.id}`}
									buttonVariant="secondary"
									onClick={() => handleDecline(request.id)}
									disabled={isPicking}
									sx={{ flex: 1 }}
								>
									Decline
								</SystemButton>
								<SystemButton
									data-testid={`request-approve-${request.id}`}
									buttonVariant="primary"
									onClick={() => handleApprove(request.id, shift.id || "")}
									disabled={isPicking}
									sx={{ flex: 1 }}
								>
									Approve
								</SystemButton>
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</SwipeableDrawer>
	);
};

export default SwapRequestDrawer;
